import { useState, useEffect } from 'react'
import { Save, Truck, Users, Settings2, Eye, EyeOff, ArrowUpDown, Receipt } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useLabelCurral } from '../../hooks/useLabelCurral'
import mapasService from '../../services/mapasService'

function Section({ icon: Icon, title, subtitle, color = 'text-blue-600', children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50">
        <div className={`p-1.5 rounded-lg bg-white border border-gray-200 ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="px-5 divide-y divide-gray-100">
        {children}
      </div>
    </div>
  )
}

function ColumnGrid({ columns, activeColumns, configKey, set }) {
  const naturalOrder = columns.map(c => c.id)

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {columns.map(col => {
        const isActive = (activeColumns || []).includes(col.id)
        return (
          <div
            key={col.id}
            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
              isActive ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'
            } ${col.obrigatoria ? 'opacity-75 cursor-not-allowed' : 'hover:border-blue-300'}`}
            onClick={() => {
              if (col.obrigatoria) return
              const current = activeColumns || []
              if (isActive) {
                set(configKey, current.filter(c => c !== col.id))
              } else {
                const updated = [...current, col.id].sort((a, b) => naturalOrder.indexOf(a) - naturalOrder.indexOf(b))
                set(configKey, updated)
              }
            }}
          >
            {isActive ? <Eye className="w-4 h-4 text-blue-600 flex-shrink-0" /> : <EyeOff className="w-4 h-4 text-gray-400 flex-shrink-0" />}
            <span className={`text-xs font-medium ${isActive ? 'text-blue-900' : 'text-gray-600'}`}>{col.label}</span>
            {col.obrigatoria && <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1 rounded">Obrig.</span>}
          </div>
        )
      })}
    </div>
  )
}

export default function RelatoriosConfig() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const labelCurral = useLabelCurral()

  const [config, setConfig] = useState({
    liberacao_curral_versao_padrao: '1',
    liberacao_curral_v1_colunas_ativas: ['lote', 'vendedor', 'curral', 'situacao', 'qtd', 'sexo', 'idade', 'crias_femea', 'crias_macho'],
    liberacao_curral_v2_colunas_ativas: ['lote', 'situacao', 'qtd', 'sexo', 'idade', 'categoria', 'brincos_gta', 'crias'],
    lista_vendedores_colunas_ativas: ['lote', 'qtd', 'tipo', 'raca', 'sexo', 'idade', 'curral', 'peso', 'arroba'],
    lista_vendedores_ordenacao: 'nome_vendedor',
    relacao_clientes_ordenacao: 'valor',
    relacao_clientes_mostrar_sexo: false,
    relacao_clientes_orientacao_pdf: 'portrait',
    relacao_clientes_mostrar_qtd_lotes: false,
    relacao_clientes_ocultar_valor_liquido: false,
    relacao_clientes_ocultar_valor_bruto: false,
    relacao_clientes_mostrar_pct_comissao: false,
    recibo_comissao_sem_centavos: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    mapasService.getConfig(orgId)
      .then(data => {
        if (!data.lista_vendedores_colunas_ativas) {
          data.lista_vendedores_colunas_ativas = ['lote', 'qtd', 'tipo', 'raca', 'sexo', 'idade', 'curral', 'peso', 'arroba']
        }
        if (!data.lista_vendedores_ordenacao) {
          data.lista_vendedores_ordenacao = 'nome_vendedor'
        }
        if (!data.liberacao_curral_v1_colunas_ativas) {
          data.liberacao_curral_v1_colunas_ativas = ['lote', 'vendedor', 'curral', 'situacao', 'qtd', 'sexo', 'idade', 'crias_femea', 'crias_macho']
        }
        if (!data.liberacao_curral_v2_colunas_ativas) {
          data.liberacao_curral_v2_colunas_ativas = ['lote', 'situacao', 'qtd', 'sexo', 'idade', 'categoria', 'brincos_gta', 'crias']
        }
        if (!data.relacao_clientes_ordenacao) {
          data.relacao_clientes_ordenacao = 'valor'
        }
        if (!data.relacao_clientes_mostrar_sexo) {
          data.relacao_clientes_mostrar_sexo = false
        }
        if (!data.relacao_clientes_orientacao_pdf) {
          data.relacao_clientes_orientacao_pdf = 'portrait'
        }
        if (data.relacao_clientes_mostrar_qtd_lotes === undefined) {
          data.relacao_clientes_mostrar_qtd_lotes = false
        }
        if (data.relacao_clientes_ocultar_valor_liquido === undefined) {
          data.relacao_clientes_ocultar_valor_liquido = false
        }
        if (data.relacao_clientes_ocultar_valor_bruto === undefined) {
          data.relacao_clientes_ocultar_valor_bruto = false
        }
        if (data.relacao_clientes_mostrar_pct_comissao === undefined) {
          data.relacao_clientes_mostrar_pct_comissao = false
        }
        setConfig(c => ({ ...c, ...data }))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [orgId])

  const set = (key, val) => setConfig(p => ({ ...p, [key]: val }))

  const handleSave = async () => {
    if (!orgId) return
    setSaving(true)
    try {
      const salvo = await mapasService.updateConfig(orgId, config)
      setConfig(p => {
        const { recibo_ultimo_numero_definir, ...resto } = p
        return { ...resto, recibo_ultimo_numero: salvo?.recibo_ultimo_numero ?? p.recibo_ultimo_numero }
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      alert('Erro ao salvar: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return (
    <div className="flex justify-center items-center h-40">
      <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-purple-600" />
          <h1 className="text-lg font-semibold text-gray-800">Configurações de Relatórios</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Salvando...' : saved ? 'Salvo ✓' : 'Salvar'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Nomenclatura Curral/Mangueira */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Nomenclatura do Curral</h3>
              <p className="text-xs text-gray-500 mt-0.5">Troca o label "Curral" por "Mangueira" em todo o sistema (lotes, relatórios, mapas, contratos)</p>
            </div>
            <div className="flex rounded-lg overflow-hidden border border-gray-300 w-52">
              <button
                type="button"
                onClick={() => {
                  const updated = { ...organizadorAtivo, label_curral: 'Curral' };
                  import('../../services/organizadoresService').then(m => m.default.update(orgId, { label_curral: 'Curral' }));
                  window.location.reload();
                }}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${(organizadorAtivo?.label_curral || 'Curral') === 'Curral' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              >
                Curral
              </button>
              <button
                type="button"
                onClick={() => {
                  import('../../services/organizadoresService').then(m => m.default.update(orgId, { label_curral: 'Mangueira' }));
                  window.location.reload();
                }}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${organizadorAtivo?.label_curral === 'Mangueira' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              >
                Mangueira
              </button>
            </div>
          </div>
        </div>

        {/* Liberação Curral */}
        <Section icon={Truck} title="Liberação Curral" subtitle="Layout, colunas e personalização do relatório" color="text-green-600">
          <div className="flex items-center justify-between py-3">
            <div className="flex-1 pr-4">
              <p className="text-sm font-medium text-gray-800">Versão padrão do layout</p>
              <p className="text-xs text-gray-500 mt-0.5">
                V1 — layout clássico com vendedor, curral e situação.
                V2 — layout de embarque com categoria, brincos/GTA e controle manual.
              </p>
            </div>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm flex-shrink-0">
              {['1', '2'].map(v => (
                <button key={v} type="button"
                  onClick={() => set('liberacao_curral_versao_padrao', v)}
                  className={`px-4 py-1.5 font-medium transition-colors ${
                    config.liberacao_curral_versao_padrao === v
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}>
                  Versão {v}
                </button>
              ))}
            </div>
          </div>
          <div className="py-3">
            <p className="text-sm font-medium text-gray-800 mb-3">Colunas visíveis — Versão 1</p>
            <ColumnGrid
              columns={[
                { id: 'lote',        label: 'Lote',        obrigatoria: true },
                { id: 'vendedor',    label: 'Vendedor',    obrigatoria: false },
                { id: 'curral',      label: labelCurral,      obrigatoria: false },
                { id: 'situacao',    label: 'Situação',    obrigatoria: false },
                { id: 'qtd',         label: 'Qtd',         obrigatoria: true },
                { id: 'sexo',        label: 'Sexo',        obrigatoria: false },
                { id: 'idade',       label: 'Idade',       obrigatoria: false },
                { id: 'crias_femea', label: 'Crias Fêmea', obrigatoria: false },
                { id: 'crias_macho', label: 'Crias Macho', obrigatoria: false },
              ]}
              activeColumns={config.liberacao_curral_v1_colunas_ativas}
              configKey="liberacao_curral_v1_colunas_ativas"
              set={set}
            />
          </div>
          <div className="py-3">
            <p className="text-sm font-medium text-gray-800 mb-3">Colunas visíveis — Versão 2</p>
            <ColumnGrid
              columns={[
                { id: 'lote',        label: 'Lote',         obrigatoria: true },
                { id: 'situacao',    label: 'Situação',     obrigatoria: false },
                { id: 'qtd',         label: 'Qtd.',         obrigatoria: true },
                { id: 'sexo',        label: 'Sexo',         obrigatoria: false },
                { id: 'idade',       label: 'Idade',        obrigatoria: false },
                { id: 'categoria',   label: 'Categoria',    obrigatoria: false },
                { id: 'brincos_gta', label: 'Brincos/GTA',  obrigatoria: false },
                { id: 'crias',       label: 'Crias',        obrigatoria: false },
              ]}
              activeColumns={config.liberacao_curral_v2_colunas_ativas}
              configKey="liberacao_curral_v2_colunas_ativas"
              set={set}
            />
          </div>
        </Section>

        {/* Listagem de Vendedores */}
        <Section icon={Users} title="Listagem de Vendedores" subtitle="Colunas e ordenação do relatório detalhado" color="text-blue-600">
          <div className="py-3">
            <p className="text-sm font-medium text-gray-800 mb-3">Colunas visíveis</p>
            <ColumnGrid
              columns={[
                { id: 'lote', label: 'Lote', obrigatoria: true },
                { id: 'qtd', label: 'Qtd', obrigatoria: true },
                { id: 'tipo', label: 'Tipo', obrigatoria: false },
                { id: 'raca', label: 'Raça', obrigatoria: false },
                { id: 'sexo', label: 'Sexo', obrigatoria: false },
                { id: 'idade', label: 'Idade', obrigatoria: false },
                { id: 'curral', label: labelCurral, obrigatoria: false },
                { id: 'peso', label: 'Peso (kg)', obrigatoria: false },
                { id: 'arroba', label: 'Total @', obrigatoria: false },
              ]}
              activeColumns={config.lista_vendedores_colunas_ativas}
              configKey="lista_vendedores_colunas_ativas"
              set={set}
              config={config}
            />
          </div>
          <div className="py-3">
            <div className="flex items-center gap-2 mb-2">
              <ArrowUpDown className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-medium text-gray-800">Ordenação dos vendedores</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'nome_vendedor', label: 'Nome (A-Z)' },
                { id: 'numero_lote', label: 'Nº do Lote' },
                { id: 'qtd_lotes', label: 'Qtd de Lotes' },
                { id: 'qtd_animais', label: 'Qtd de Animais' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => set('lista_vendedores_ordenacao', opt.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                    config.lista_vendedores_ordenacao === opt.id
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </Section>

        {/* Recibos */}
        <Section icon={Receipt} title="Recibos" subtitle="Recibos de comissão gerados pelos mapas do comprador e do vendedor" color="text-emerald-600">
          <div className="py-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => set('recibo_comissao_sem_centavos', !config.recibo_comissao_sem_centavos)}
                className={`relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${config.recibo_comissao_sem_centavos ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${config.recibo_comissao_sem_centavos ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Remover os centavos do recibo de comissão</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Ao gerar o recibo de comissão pelo mapa do comprador ou do vendedor, o valor sai sem centavos (ex.: R$ 511,84 vira R$ 511,00),
                  no PDF e na tela de Recibos. Vale só para recibos novos. Ao ativar ou desativar, o log de auditoria registra, e cada recibo gerado assim também.
                </p>
              </div>
            </label>
          </div>
          <div className="py-3 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-800">Numeração dos recibos</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Data e hora gera o número pelo momento da emissão (ex.: 2610281530). Sequencial usa 1, 2, 3... e as duas vias saem sempre com o mesmo número.
            </p>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm w-fit mt-2">
              {[{ v: 'data_hora', l: 'Data e hora' }, { v: 'sequencial', l: 'Sequencial' }].map(o => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set('recibo_numeracao', o.v)}
                  className={`px-3 py-1.5 font-medium transition-colors ${(config.recibo_numeracao || 'data_hora') === o.v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            {config.recibo_numeracao === 'sequencial' && (
              <div className="mt-3">
                <label className="text-xs font-medium text-gray-700">Último número emitido</label>
                <input
                  type="number"
                  min="0"
                  value={config.recibo_ultimo_numero ?? 0}
                  onChange={e => setConfig(p => ({
                    ...p,
                    recibo_ultimo_numero: e.target.value,
                    recibo_ultimo_numero_definir: e.target.value === '' ? 0 : parseInt(e.target.value, 10),
                  }))}
                  className="mt-1 block w-40 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  O próximo recibo sai com o número seguinte (ex.: informando 150, o próximo é 151). Vale para recibos avulsos e de comissão dos mapas.
                </p>
              </div>
            )}
          </div>
        </Section>

        {/* Relação de Clientes */}
        <Section icon={Users} title="Relação de Clientes" subtitle="Ordenação e quebra por sexo de compradores/vendedores" color="text-indigo-600">
          <div className="flex items-center justify-between py-3">
            <div className="flex-1 pr-4">
              <p className="text-sm font-medium text-gray-800">Ordenação padrão</p>
              <p className="text-xs text-gray-500 mt-0.5">Como ordenar a relação de compradores/vendedores</p>
            </div>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm flex-shrink-0">
              {[['alfabetica', 'Alfabética'], ['valor', 'Maior Valor']].map(([v, lbl]) => (
                <button key={v} type="button" onClick={() => set('relacao_clientes_ordenacao', v)}
                  className={`px-3 py-1.5 font-medium transition-colors ${config.relacao_clientes_ordenacao === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div className="py-3 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <ArrowUpDown className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-medium text-gray-800">Exibição por sexo</p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => set('relacao_clientes_mostrar_sexo', !config.relacao_clientes_mostrar_sexo)}
                className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${config.relacao_clientes_mostrar_sexo ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${config.relacao_clientes_mostrar_sexo ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">Mostrar quebra por sexo (F/M)</p>
                <p className="text-xs text-gray-500 mt-0.5">Adiciona coluna mostrando quantidade de fêmeas e machos</p>
              </div>
            </label>
          </div>
          <div className="py-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-4">
                <p className="text-sm font-medium text-gray-800">Orientação do PDF</p>
                <p className="text-xs text-gray-500 mt-0.5">Imprimir em vertical ou horizontal</p>
              </div>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm flex-shrink-0">
                {[['portrait', 'Vertical'], ['landscape', 'Horizontal']].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => set('relacao_clientes_orientacao_pdf', v)}
                    className={`px-3 py-1.5 font-medium transition-colors ${config.relacao_clientes_orientacao_pdf === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="py-3 border-t border-gray-100 space-y-3">
            <p className="text-sm font-medium text-gray-800">Colunas de valores</p>
            {[
              { key: 'relacao_clientes_mostrar_qtd_lotes', label: 'Mostrar coluna Qtd de Lotes', desc: 'Exibe a quantidade de lotes comprados/vendidos por cliente' },
              { key: 'relacao_clientes_ocultar_valor_liquido', label: 'Ocultar coluna Valor Líquido', desc: 'Remove a coluna de valor líquido do relatório' },
              { key: 'relacao_clientes_ocultar_valor_bruto', label: 'Ocultar coluna Valor Bruto', desc: 'Remove a coluna de valor bruto do relatório' },
              { key: 'relacao_clientes_mostrar_pct_comissao', label: 'Mostrar coluna % Média de Comissão', desc: 'Exibe o percentual médio de comissão pago pelo cliente no leilão' },
            ].map(({ key, label, desc }) => (
              <label key={key} className="flex items-center gap-3 cursor-pointer">
                <div
                  onClick={() => set(key, !config[key])}
                  className={`relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${config[key] ? 'bg-blue-600' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${config[key] ? 'translate-x-4' : 'translate-x-0'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                </div>
              </label>
            ))}
          </div>
        </Section>
      </div>
    </div>
  )
}
